import { useEffect, useState, useRef } from 'react';

import EditSlide from './EditSlide';

import constants from './constants.json';
import { replaceUnderscore } from './utils';
import { userHasPermission } from '../../utils';
import { getPlaylistData } from '../ContentManagement/controller';

import errorIcon from '../../images/icons/contentManagement/error-icon.svg';
import expandedIcon from '../../images/icons/menu/expanded.svg';
import landscapeIcon from '../../images/icons/screens/landscape.svg';
import mobileIcon from '../../images/icons/screens/mobile.svg';
import portraitIcon from '../../images/icons/screens/portrait-left.svg';
import imageIcon from '../../images/icons/image.svg';
import linkIcon from '../../images/icons/link.svg';
import textIcon from '../../images/icons/ts-icon.svg';
import videoIcon from '../../images/icons/video.svg';

const IMAGES_MAP = {
    landscape: landscapeIcon,
    mobile: mobileIcon,
    portrait: portraitIcon,
    image: imageIcon,
    video: videoIcon,
    text: textIcon,
    website: linkIcon
}

const ContentBrowsing = ({ customerId, slides, selected, selectedLocationId, folderName, setIsOpenContentModal, editStatus, setEditStatus, setRefreshKey, updatedBy, role, slideData, deviceType, selectCallback, permissions, setSlideData }) => {
    const [filterText, setFilterText] = useState('');
    const [visibleSlides, setVisibleSlides] = useState(slides ? slides : []);
    const [selectedIndex, setSelectedIndex] = useState(null);
    // Multi-select dropdown states
    const [selectedDeviceTypes, setSelectedDeviceTypes] = useState(deviceType ? [deviceType] : ['landscape', 'portrait', 'mobile']);
    const [selectedContentTypes, setSelectedContentTypes] = useState(['image', 'messaging', 'custom', 'link']);

    const [editableContents, setEditableContents] = useState(true);
    const [playlists, setPlaylists] = useState([]);

    // These folders are mainstays in the Content Library for each customer
    const customerFolders = ['shared_folder', 'branding_folder'];

    useEffect(() => {
        getPlaylistData(customerId, permissions['playlistFull'])
        .then(data => {
            setPlaylists(data)
        })
        .catch((error) => {
            console.error('Something went wrong:', error);
        });

        return;
    }, []);

    useEffect(() => {
        let camelCase = folderName.replace(/_([a-z])/g, (match, group1) => {
            return group1.toUpperCase();
        });
        if (customerFolders.includes(folderName) && !userHasPermission(camelCase, permissions)) {
            setEditableContents(false);
        } else {
            // If folderName is not one of the customerFolders, it is probably "My folder" or a location user has access to.
            setEditableContents(true);
        }
    }, [folderName]);

    // Change visible slides when new slides object comes in
    useEffect(() => {
        setSelectedIndex(null);
        // To avoid showing empty slides when city,zip key is clicked without the actual location name
        if (selected.sub) {
            setVisibleSlides(slides ? slides : []);
        }
        else if (constants.NAME_MAP[selected.main]) {
            setVisibleSlides(slides ? slides : []);
        }
    }, [slides]);

    // Run handleFilter after selectedDeviceTypes or selectedContentTypes change for filtering
    useEffect(() => {
        handleFilter();
    }, [filterText, selectedDeviceTypes, selectedContentTypes]);

    // Filtering function
    const handleFilter = () => {
        const tempSlides = [...slides || []]
        // Add more filter logic here as needed
        const filtered = tempSlides.filter(slide => {
            const deviceMatch = Array.isArray(slide.deviceTypes) && selectedDeviceTypes.length > 0
                ? slide.deviceTypes.some(dt => selectedDeviceTypes.includes(dt))
                : true;
            return (
                slide.label.toLowerCase().includes(filterText.toLowerCase()) &&
                selectedContentTypes.includes(slide.type) &&
                deviceMatch
            );
        });
        setVisibleSlides(filtered);
    };

    const handleSelect = () => {
        selectCallback(visibleSlides[selectedIndex]);
        setIsOpenContentModal(false);
    }

    return (
        <div className="ContentBrowsing">
            {
                editStatus !== null
                ? <EditSlide
                    setEditStatus={setEditStatus}
                    customerId={customerId}
                    slideToEdit={slideData ? slideData : editStatus === 'add' ? {} : selectedIndex !== null ? visibleSlides[selectedIndex] : {}}
                    updatedBy={updatedBy}
                    selectedLocationId={selectedLocationId}
                    folderName={folderName}
                    setRefreshKey={setRefreshKey}
                    role={role}
                    selectCallback={selectCallback}
                    setIsOpenContentModal={setIsOpenContentModal}
                    permissions={permissions}
                    playlists={playlists}
                    setSlideData={setSlideData}
                />
                : <>
                    <div className="clm-content-header">
                        <h2>Browsing {constants.NAME_MAP[folderName]|| replaceUnderscore(folderName, ', ')} </h2>
                        <button className="clm-close-btn" aria-label="Close Content library" onClick={() => setIsOpenContentModal(false)}>&times;</button>
                    </div>
                    <div className="clm-content-filters">
                        <span className="clm-content-search-label">Search</span>
                        <input
                            type="text"
                            className="clm-content-search-input"
                            placeholder="Enter a search term"
                            value={filterText}
                            onChange={e => setFilterText(e.target.value)}
                        />
                        <MultiSelectDropdown
                            label="Device Types"
                            options={constants.DEVICE_TYPE_OPTIONS}
                            selected={selectedDeviceTypes}
                            setSelected={setSelectedDeviceTypes}
                        />
                        <MultiSelectDropdown
                            label="Content Types"
                            placeholder="Select content types"
                            options={constants.CONTENT_TYPE_OPTIONS}
                            selected={selectedContentTypes}
                            setSelected={setSelectedContentTypes}
                        />
                    </div>
                    <div className="clm-content-slides">
                        {visibleSlides.length === 0 ? (
                            <div className="no-slides">No content items found.</div>
                        ) : (
                            visibleSlides.map((slide, idx) => (
                                <div
                                    className={`clm-slide-container ${selectedIndex === idx ? ' selected' : ''}`}
                                    key={slide.id}
                                >
                                    <SlideItem slide={slide} idx={idx} selectedIndex={selectedIndex} onClick={() => setSelectedIndex(idx)} />
                                </div>
                            ))
                        )}
                    </div>
                    <div className="clm-content-actions">
                        <button className={`clm-action-btn ${selectedIndex === null || !editableContents ? 'disabled-button' : ''}`} disabled={selectedIndex === null && !editableContents} onClick={() => setEditStatus('edit')}>Edit item</button>
                        <button className={`clm-action-btn ${!editableContents ? 'disabled-button' : ''}`} onClick={() => setEditStatus('add')}>Add item</button>
                        <button className={`clm-action-btn ${selectedIndex === null || selectCallback === undefined ? 'disabled-button' : ''}`} disabled={selectedIndex === null || selectCallback === undefined} onClick={handleSelect}>Select</button>
                    </div>
                </>
            }
        </div>
    );
};

const SlideItem = ({ slide, idx, selectedIndex, onClick }) => {
    // Truncate label to at most 20 characters
    const truncateLabel = (label, maxLength = 25) => {
        if (!label) return '';
        return label.length > maxLength ? label.slice(0, maxLength) + '…' : label;
    };

    return (
        <div className={`clm-slide-item`} onClick={onClick}>
            <div className={`clm-slide-image-wrapper`}>
                <IFrame url={slide.url} type={slide.type} onClick={onClick} />
            </div>
            <div className="clm-slide-label">
                {truncateLabel(slide.label)}
            </div>
        </div>
    );
};


const IFrame = ({ url, type, onClick }) => {
    const ref = useRef();
    
    if (type === 'image') {
        return <img src={url} ref={ref} className="clm-slide-image" onClick={onClick} onError={
            e => { 
                e.target.onerror = null;
                e.target.src = errorIcon;
                e.target.className = "clm-slide-image clm-slide-image-error";
            }} 
        />;
    }
    // For iframe, wrap in a div to allow pointer cursor and click handler
    return (
        <div className="iframe-container" onClick={onClick}>
            <iframe src={url} ref={ref} className="clm-slide-image" frameBorder="0" tabIndex={-1} loading="lazy" style={{ pointerEvents: 'none' }} />
        </div>
    );
}


const MultiSelectDropdown = ({ label, options, selected, setSelected }) => {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="clm-multiselect-dropdown" ref={dropdownRef}>
            <div className="clm-dropdown-label" onClick={() => setOpen(!open)}>
                {label}
                <img className="clm-dropdown-arrow" src={expandedIcon} />
            </div>
            {open && (
                <div className="clm-dropdown-menu">
                    {options.map(option => (
                        <label key={option.value} className="clm-dropdown-option">
                            <input
                                type="checkbox"
                                checked={selected.includes(option.value)}
                                onChange={e => {
                                    if (e.target.checked) {
                                        setSelected([...selected, option.value]);
                                    } else {
                                        setSelected(selected.filter(t => t !== option.value));
                                    }
                                }}
                            />
                            <span className="clm-checkbox-custom"></span>
                            <img src={IMAGES_MAP[option.image]} className="clm-option-icon" />
                            {option.label}
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ContentBrowsing;
